/**
 * 카드·히어로 배지 규칙 (v2, 2026-09-21 대표 결정 반영).
 *
 * ★ 2026-09-22 대표 지시: 배지도 지도 배너와 같은 순서(할인율 → 할인금액 → 가격 → 기타 조건, 사은품 제외).
 *   서버가 banner_text/banner_subtext(services/benefits.build_banner_text)를 주면 그것을 그대로 쓴다:
 *     1. banner_text → osolid  2. banner_subtext → ylw  3. 분양예정 → o   (최대 3)
 *   ★ 2026-09-22 대표("원분양가와 같다면 혜택이 될 수 없지"): banner_kind === 'price'(원분양가/할인가 X억~)면
 *     본문 배지는 key 'price' · 중립 톤(mute) — '대표혜택' 라벨·오렌지 솔리드를 쓰지 않는다.
 *   아래 1~3 은 banner_* 필드가 없는 구 응답 폴백.
 *
 * (폴백) 최대 3개, 우선순위:
 *   1. 대표혜택 — benefit_headline.text (서버 services/benefits.py 가 계산) → osolid
 *   2. 할인율   — ①이 price_cut 이 아닐 때만. discount_benefits 중 kind='price_cut' 의 discount_rate 최대값
 *                → ylw "최대 N% 할인" (map API 는 discount_benefits 가 없어 생략됨)
 *   3. 분양예정 — isUpcomingBadge(status, is_pre_registration) 일 때만 → o
 *
 * 정책: 분양중·마감임박·미분양·임대·사전등록 등 다른 상태 배지는 절대 만들지 않는다.
 * (README 3장 상태 배지 8종은 대표 결정 "상태 배지는 '분양예정'만" 으로 대체됨)
 */
import type { BenefitHeadline, DiscountBenefit, DiscountBenefitRestricted } from '../../services/api';
import { isUpcomingBadge, reopenBadgeLabel, UPCOMING_BADGE_LABEL } from './formatters';

export type BadgeTone = 'o' | 'osolid' | 'ylw' | 'blue' | 'green' | 'red' | 'mute' | 'violet';

export interface PropertyBadge {
  key: 'headline' | 'price' | 'sub' | 'discount' | 'upcoming' | 'reopen' | 'soldout';
  label: string;
  tone: BadgeTone;
  /** 스크린리더용 설명 (예: "대표혜택") */
  ariaLabel?: string;
}

export interface BadgeSource {
  status?: string | null;
  is_pre_registration?: boolean | null;
  /** 즉시입주 잔여세대(2026-09-28) — 분류 배지라 맨 앞에 둔다 */
  is_reopen?: boolean | null;
  benefit_headline?: BenefitHeadline | null;
  banner_text?: string | null;
  banner_subtext?: string | null;
  /** banner_text 단계: rate|amount|price|manual|benefit|null (서버 build_banner_text source) */
  banner_kind?: string | null;
  discount_benefits?: DiscountBenefit[] | DiscountBenefitRestricted | null;
}

export const MAX_BADGES = 3;

/** discount_benefits 중 분양가 인하(price_cut) 최대 할인율. 없으면 null. */
export function maxPriceCutRate(benefits: BadgeSource['discount_benefits']): number | null {
  if (!Array.isArray(benefits)) return null;
  let max: number | null = null;
  for (const b of benefits) {
    if (b.kind !== 'price_cut') continue;
    const r = typeof b.discount_rate === 'number' ? b.discount_rate : null;
    if (r !== null && r > 0 && (max === null || r > max)) max = r;
  }
  return max;
}

export function buildBadges(p: BadgeSource | null | undefined): PropertyBadge[] {
  if (!p) return [];
  const out: PropertyBadge[] = [];
  const reopen = reopenBadgeLabel(p.status, p.is_reopen === true);
  if (reopen) out.push({ key: 'reopen', label: reopen, tone: 'blue', ariaLabel: '분류' });

  // 서버 단일 기준(배너와 동일 문구) — 필드가 있으면(null 포함) 폴백하지 않는다
  if (p.banner_text !== undefined || p.banner_subtext !== undefined) {
    const main = (p.banner_text ?? '').trim();
    const sub = (p.banner_subtext ?? '').trim();
    if (main) {
      out.push(
        p.banner_kind === 'price'
          ? { key: 'price', label: main, tone: 'mute', ariaLabel: '분양가' }
          : { key: 'headline', label: main, tone: 'osolid', ariaLabel: '대표혜택' },
      );
    }
    if (sub && sub !== main) out.push({ key: 'sub', label: sub, tone: 'ylw', ariaLabel: '혜택' });
    if (isUpcomingBadge(p.status, p.is_pre_registration === true)) {
      out.push({ key: 'upcoming', label: UPCOMING_BADGE_LABEL, tone: 'o', ariaLabel: '분양 상태' });
    }
    return out.slice(0, MAX_BADGES);
  }

  const headline = p.benefit_headline;
  const headlineText = (headline?.text ?? '').trim();
  if (headlineText) {
    out.push({ key: 'headline', label: headlineText, tone: 'osolid', ariaLabel: '대표혜택' });
  }

  if (headline?.kind !== 'price_cut') {
    const rate = maxPriceCutRate(p.discount_benefits);
    if (rate !== null) {
      out.push({ key: 'discount', label: `최대 ${Math.floor(rate)}% 할인`, tone: 'ylw', ariaLabel: '할인율' });
    }
  }

  if (isUpcomingBadge(p.status, p.is_pre_registration === true)) {
    out.push({ key: 'upcoming', label: UPCOMING_BADGE_LABEL, tone: 'o', ariaLabel: '분양 상태' });
  }

  return out.slice(0, MAX_BADGES);
}
